import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';
import { scaleQuantize } from '@visx/scale';
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import * as React from 'react';
import { useState } from 'react';

import { Button, type ButtonProps } from '~/components/ui/button';
import { cn } from '~/styles/extended-theme.ts';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

interface CarouselProps {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins,
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        ref={ref}
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
});
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef} className="overflow-hidden">
        <div
          ref={ref}
          className={cn(
            'flex',
            orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
            className,
          )}
          {...props}
        />
      </div>
    );
  },
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          'min-w-0 shrink-0 grow-0 basis-full',
          orientation === 'horizontal' ? 'pl-4' : 'pt-4',
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute h-8 w-8 rounded-full',
          orientation === 'horizontal'
            ? '-left-12 top-1/2 -translate-y-1/2'
            : '-top-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <span className="sr-only">Previous slide</span>
      </Button>
    );
  },
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'absolute h-8 w-8 rounded-full',
          orientation === 'horizontal'
            ? '-right-12 top-1/2 -translate-y-1/2'
            : '-bottom-12 left-1/2 -translate-x-1/2 rotate-90',
          className,
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <ArrowRightIcon className="h-4 w-4" />
        <span className="sr-only">Next slide</span>
      </Button>
    );
  },
);
CarouselNext.displayName = 'CarouselNext';

// TODO move to a util?
const range = (length: number) => Array.from({ length }, (_, i) => i);

const useScrollIntoView = (api: CarouselApi | undefined) => {
  // defaulting these to 1 keeps the scale happy
  const itemCount = api?.slideNodes().length ?? 1;
  const snapPointCount = api?.scrollSnapList().length ?? 1;

  // maps item index to snap point index
  const scale = scaleQuantize({
    domain: [0, itemCount - 1],
    range: range(snapPointCount),
  });

  const scrollIntoView = (index: number) => {
    const snapIndex = scale(index);
    api?.scrollTo(snapIndex);
  };

  return scrollIntoView;
};

type CarouselControlsProps = {
  buttonClassName?: HTMLButtonElement['className'];
  activeButtonClassName?: HTMLButtonElement['className'];
  selected?: number;
  onSelected?: (i: number) => void;
} & React.HtmlHTMLAttributes<HTMLDivElement>;

const CarouselControls = React.forwardRef<HTMLDivElement, CarouselControlsProps>(
  (
    {
      className,
      buttonClassName = 'bg-white bg-opacity-60',
      activeButtonClassName = 'bg-white bg-opacity-100',
      selected: externalSelected,
      onSelected: externalOnSelected,
      ...props
    },
    ref,
  ) => {
    const controlled = externalSelected !== undefined && externalOnSelected !== undefined;
    const { api } = useCarousel();
    const scrollIntoView = useScrollIntoView(api);
    const [selectedIndex, setSelectedIndex] = useState(controlled ? externalSelected : 0);

    React.useEffect(() => {
      if (controlled) {
        setSelectedIndex(externalSelected);
      }
    }, [externalSelected, controlled]);

    const itemCount = api?.slideNodes().length ?? 0;

    const inView = (index: number) => selectedIndex === index;
    const handleClick = (index: number) => () => {
      scrollIntoView(index);
      setSelectedIndex(index);

      if (controlled) {
        externalOnSelected(index);
      }
    };

    if (itemCount <= 1) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'absolute bottom-6 left-16 right-16 flex h-2 gap-1 overflow-hidden rounded-full',
          className,
        )}
        {...props}
      >
        {range(itemCount).map((i) => (
          <button
            key={i}
            onClick={handleClick(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={cn('flex-grow', inView(i) ? activeButtonClassName : buttonClassName)}
            type="button"
          />
        ))}
      </div>
    );
  },
);
CarouselControls.displayName = 'CarouselControls';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselControls,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  useScrollIntoView,
};
